import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { PencilIcon } from '../ui/Icons';

interface PersonalItem {
  id: string;
  name: string;
  description: string;
  category: string;
  location: string;
  recipient: string;
  specialInstructions: string;
  documents: string[];
  estimatedValue?: number;
  insuranceInfo?: string;
}

interface CategorySummary {
  name: string;
  items: PersonalItem[];
  totalValue: number;
}

const Frame803: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSessionPersistence();
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [totalValue, setTotalValue] = useState(0);

  useEffect(() => {
    const items = session.taskProgress?.personal?.answers?.personalItems || [];
    
    // Group items by category
    const groupedItems = items.reduce((acc: { [key: string]: PersonalItem[] }, item: PersonalItem) => {
      const category = item.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});

    // Calculate summaries for each category
    const summaries = Object.entries(groupedItems).map(([name, items]) => ({
      name,
      items,
      totalValue: items.reduce((sum, item) => sum + (item.estimatedValue || 0), 0)
    }));

    // Sort categories by total value
    summaries.sort((a, b) => b.totalValue - a.totalValue);

    setCategories(summaries);
    setTotalValue(summaries.reduce((sum, cat) => sum + cat.totalValue, 0));
  }, [session.taskProgress?.personal?.answers?.personalItems]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleEditCategory = (category: string) => {
    navigate('/frame-801', { state: { selectedCategory: category } });
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title="Personal Belongings Summary"
        />

        <div className="max-w-4xl mx-auto">
          {/* Total Value */}
          <div className="bg-white rounded-lg border border-stone-200 p-6 mb-8">
            <h2 className="text-2xl font-semibold text-stone-800 mb-2">
              Total Estimated Value
            </h2>
            <div className="text-3xl font-bold text-cyan-600">
              {formatCurrency(totalValue)}
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-8">
            {categories.map(category => (
              <div key={category.name} className="bg-white rounded-lg border border-stone-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-stone-800">
                    {category.name}
                  </h2>
                  <button
                    onClick={() => handleEditCategory(category.name)}
                    className="text-cyan-600 hover:text-cyan-700 flex items-center gap-2"
                  >
                    <PencilIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">Edit Items</span>
                  </button>
                </div>

                <div className="text-lg font-medium text-cyan-600 mb-4">
                  {formatCurrency(category.totalValue)}
                </div>

                <div className="space-y-4">
                  {category.items.map(item => (
                    <div key={item.id} className="border-t border-stone-100 pt-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-stone-800">{item.name}</h3>
                          {item.description && (
                            <p className="text-sm text-stone-600 mt-1">{item.description}</p>
                          )}
                          {item.location && (
                            <p className="text-sm text-stone-600 mt-1">
                              Location: {item.location}
                            </p>
                          )}
                        </div>
                        {item.estimatedValue !== undefined && (
                          <div className="text-stone-600 font-medium">
                            {formatCurrency(item.estimatedValue)}
                          </div>
                        )}
                      </div>
                      {item.specialInstructions && (
                        <div className="mt-2 text-sm text-stone-600">
                          <strong>Special Instructions:</strong>
                          <p className="mt-1">{item.specialInstructions}</p>
                        </div>
                      )}
                      {item.insuranceInfo && (
                        <div className="mt-2 text-sm text-stone-600">
                          <strong>Insurance Information:</strong>
                          <p className="mt-1">{item.insuranceInfo}</p>
                        </div>
                      )}
                      {item.documents && item.documents.length > 0 && (
                        <div className="mt-2 text-sm text-stone-600">
                          <strong>Documents:</strong>
                          <ul className="mt-1 list-disc list-inside">
                            {item.documents.map((doc, index) => (
                              <li key={index}>{doc}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {categories.length === 0 && (
              <div className="text-center py-12 text-stone-600">
                <p className="text-lg">No personal belongings added yet.</p>
                <button
                  onClick={() => navigate('/frame-801')}
                  className="mt-4 bg-cyan-600 text-white px-6 py-2 rounded-lg hover:bg-cyan-700 transition-colors"
                >
                  Add Items
                </button>
              </div>
            )}
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Personal Belongings"
        subtitle="Overall Summary"
        progressPercent={66}
        onBack={() => navigate('/frame-801')}
        onNext={() => navigate('/frame-802')}
        nextText="Complete →"
        backText="← Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame803;

